import React, { Component } from 'react';
import moment, { Moment } from 'moment';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import Select from 'react-select';
import { compose } from 'redux';
import { Location } from 'history';
import { toast, ToastContainer } from 'react-toastify';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { DateRangePicker, FocusedInputShape } from 'react-dates';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PrimaryButton } from '../PrimaryButton';
import { Notification } from '../Notification';
import { Spinner } from '../Spinner';
import { SelectedBuilding } from '../../store/building/types';
import { AlertsActions } from '../../store';
import { AlertType, RegisterSlugType, SubscriberType } from '../../store/alerts/types';
import { AlertConfigurationConditions } from './components/AlertConfigurationConditions';
import { AlertConfigurationSubscribers } from './components/AlertConfigurationSubscribers/AlertConfigurationSubscribers';
import { getEditAlertFromUrlParams } from '../../utils/urlParams';
import './AlertConfiguration.css';

const customStyles = {
  control: (styles: any) => ({
    ...styles,
    height: '2.5rem',
    border: '1px solid #abadae',
    marginTop: '0.4rem',
    boxShadow: '0 1px 0 0 rgba(46, 50, 53, 0.1)',
    fontWeight: '400',
    textAlign: 'left',
  }),
  option: (provided: any) => ({
    ...provided,
    fontWeight: '400',
    textAlign: 'left',
  }),
};
const alertTypeOptions = [
  {
    value: 'schedule',
    label: 'Schedule Monitoring',
  },
  {
    value: 'monitoring',
    label: 'Monitoring',
  },
  {
    value: 'configuration',
    label: 'Configuration',
  },
];
const severityLevelOptions = [
  {
    value: 'beta',
    label: 'Beta',
  },
  {
    value: 'critical',
    label: 'Critical',
  },
  {
    value: 'high',
    label: 'High',
  },
  {
    value: 'medium',
    label: 'Medium',
  },
  {
    value: 'low',
    label: 'Low',
  },
  {
    value: 'info',
    label: 'Info',
  },
];

const initialState = {
  name: '',
  message: '',
  description: '',
  alertType: '',
  severityLevel: '',
  isActive: '',
  startDate: '',
  endDate: '',
  threshold: '',
  autoResolveThreshold: '',
  touched: {
    name: false,
    message: false,
    description: true,
    threshold: false,
    autoResolveThreshold: false,
  },
  errors: {
    name: false,
    message: false,
    description: false,
    threshold: false,
    autoResolveThreshold: false,
  },
  focusedInput: (null as unknown) as FocusedInputShape,
  conditions: [],
  subscribers: [],
  contexts: [],
  alert_uid_hash: '',
  subscriber_update_api_location: '',
  context_api_location: '',
  condition_api_location: '',
};
const falseFunc = () => false;

export interface Props {
  building: SelectedBuilding;
  isLoading: boolean;
  alertsError: string;
  alertCreated: boolean;
  alertEdited: boolean;
  createAlert(
    building: string,
    name: string,
    message: string,
    description: string,
    alertType: string,
    severityLevel: string,
    isActive: boolean,
    startDate: string,
    endDate: string,
    threshold: string,
    autoResolveThreshold: string,
    conditions: any[],
    subscribers: any[],
    contexts: any[],
  ): void;
  updateAlert(
    alert_uid_hash: string,
    building: string,
    name: string,
    message: string,
    description: string,
    alertType: string,
    severityLevel: string,
    isActive: boolean,
    startDate: string,
    endDate: string,
    threshold: string,
    autoResolveThreshold: string,
  ): void;
  getDataTags(building: string): void;
  getSubscribers(building: string): void;
  updateSubscribers(api_location: string, subscribers: SubscriberType, operation: string): void;
  createNewContext(api_location: string, context: string): void;
  updateContext(api_location: string, context: string): void;
  removeContext(api_location: string): void;
  createNewCondition(api_location: string, condition: ConditionType): void;
  updateCondition(api_location: string, name: string, value: string): void;
  removeCondition(api_location: string): void;
  fetchAlert(building: string, alertId: string): AlertType;
  dataTags: RegisterSlugType[];
  subscriberUsers: SubscriberType[];
  location: Location;
  clearAlertsError(): void;
  clearCreateAlertConditionSuccess(): void;
  clearEditAlertConditionSuccess(): void;
}

export interface State {
  name: string;
  message: string;
  description: string;
  alertType: string;
  severityLevel: string;
  isActive: string;
  startDate: string | null;
  endDate: string | null;
  threshold: string;
  autoResolveThreshold: string;
  touched: {
    name: boolean;
    message: boolean;
    description: boolean;
    threshold: boolean;
    autoResolveThreshold: boolean;
  };
  errors: {
    name: boolean;
    message: boolean;
    description: boolean;
    threshold: boolean;
    autoResolveThreshold: boolean;
  };
  focusedInput: FocusedInputShape | null;
  conditions: ConditionType[];
  subscribers: StateSubscriberType[];
  contexts: ContextType[];
  alert_uid_hash: string;
  subscriber_update_api_location: string;
  context_api_location: string;
  condition_api_location: string;
}

interface ConditionType {
  name: string;
  data_point_tag: string;
  comparison_operator: string;
  limit: string;
  measurement_unit: string;
  rolling_average_minutes: string;
  message: string;
}

interface StateSubscriberType {
  method: string;
  user: string;
  name: string;
  email: string;
  phone_number: string;
  frequency: number;
  deactivate_until: string | undefined;
  operation: string;
}

interface ContextType {
  data_point_tag: string;
}

interface DataTagOptionType {
  value: string;
  label: string;
}

interface SubscriberOptionType {
  value: number;
  label: string;
}

export declare type ComposedProps = Props & RouteComponentProps;

export class AlertConfiguration extends Component<ComposedProps, State> {
  state = initialState;

  async componentDidMount() {
    const {
      building,
      getDataTags,
      getSubscribers,
      location: { search },
      fetchAlert,
    } = this.props;

    await getDataTags(building.identifier);
    await getSubscribers(building.identifier);

    const editAlertId = getEditAlertFromUrlParams(search);
    if (editAlertId !== '') {
      const alert = (await fetchAlert(building.identifier, editAlertId)) as AlertType;
      this.populateAlertToEdit(alert);
    }
  }

  componentDidUpdate() {
    this.props.clearCreateAlertConditionSuccess();
    this.props.clearEditAlertConditionSuccess();
  }

  populateAlertToEdit(alert: AlertType) {
    const {
      alert_type,
      severity_level,
      is_active,
      auto_resolve_threshold,
      schedule_start_date,
      schedule_end_date,
      uid_hash,
      subscriber_update_api_location,
      context_api_location,
      condition_api_location,
    } = alert;

    const strIsActive = is_active ? 'yes' : 'no';

    this.setState((state) => ({
      ...state,
      ...alert,
      alertType: alert_type,
      severityLevel: severity_level,
      isActive: strIsActive,
      autoResolveThreshold: auto_resolve_threshold,
      startDate: schedule_start_date,
      endDate: schedule_end_date,
      alert_uid_hash: uid_hash,
      subscriber_update_api_location,
      context_api_location,
      condition_api_location,
    }));
  }

  validDataTags = (dataTags: RegisterSlugType[]) => {
    const validDataTags: DataTagOptionType[] = [];

    dataTags.map((dataSet) => {
      validDataTags.push({
        value: dataSet.data_point_tag,
        label: dataSet.name,
      });
    });

    return validDataTags;
  };

  validSubscribers = (subscribers: SubscriberType[]) => {
    const validSubscribers: SubscriberOptionType[] = [];

    subscribers.map((subscriber) => {
      validSubscribers.push({
        value: subscriber.user,
        label: subscriber.name,
      });
    });

    return validSubscribers;
  };

  changeDates = (startDate: Moment | null, endDate: Moment | null) => {
    const formattedStartDate = startDate ? moment(startDate).utc().format('YYYY-MM-DD') : null;
    const formattedEndDate = endDate ? moment(endDate).utc().format('YYYY-MM-DD') : null;

    this.setState((state) => ({
      ...state,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    }));
  };

  handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist();
    const { name, value } = e.target;

    this.setState((state) => ({
      ...state,
      [name]: value,
    }));
  };

  onSelectChange = (option: any, name: string) => {
    this.setState((state) => ({
      ...state,
      [name]: option,
    }));
  };

  handleBlur = (field: string) => {
    this.setState((state) => ({
      ...state,
      touched: {
        ...state.touched,
        [field]: true,
      },
    }));

    this.validateFields();
  };

  addConditionRow = () => {
    const blankNewCondition = {
      name: '',
      data_point_tag: '',
      comparison_operator: 'eq',
      limit: '',
      measurement_unit: '',
      rolling_average_minutes: '',
      message: '',
    };

    this.setState((state) => ({
      ...state,
      conditions: [...state.conditions, blankNewCondition],
    }));
  };

  addSubscriberRow = () => {
    const blankNewSubscriber = {
      method: '',
      user: '',
      name: '',
      email: '',
      phone_number: '',
      frequency: 0,
      deactivate_until: undefined,
      operation: '',
    };

    this.setState((state) => ({
      ...state,
      subscribers: [...state.subscribers, blankNewSubscriber],
    }));
  };

  addContextRow = () => {
    const blankNewContext = {
      data_point_tag: '',
    };

    this.setState((state) => ({
      ...state,
      contexts: [...state.contexts, blankNewContext],
    }));
  };

  removeConditionRow = (index: number, api_location: string) => {
    const { alert_uid_hash, conditions } = this.state;
    const { removeCondition } = this.props;

    if (alert_uid_hash) {
      // If in edit alert page, send API request to remove the context
      removeCondition(api_location);
    }

    let rowsCopy = JSON.parse(JSON.stringify(conditions));
    rowsCopy.splice(index, 1)[0];
    this.setState((state) => ({
      ...state,
      conditions: rowsCopy,
    }));
  };

  removeContextRow = (index: number, contexts: any[]) => {
    const { alert_uid_hash } = this.state;
    const { removeContext } = this.props;

    if (alert_uid_hash) {
      // If in edit alert page, send API request to remove the context
      const apiLocation = contexts[index].api_location;
      removeContext(apiLocation);
    }

    let rowsCopy = JSON.parse(JSON.stringify(contexts));
    rowsCopy.splice(index, 1)[0];
    this.setState((state) => ({
      ...state,
      contexts: rowsCopy,
    }));
  };

  removeRow = (index: number, rows: any[], arrayName: string) => {
    const { updateSubscribers } = this.props;
    const { subscriber_update_api_location } = this.state;
    let rowsCopy = JSON.parse(JSON.stringify(rows));
    const deleted = rowsCopy.splice(index, 1)[0];
    this.setState((state) => ({
      ...state,
      [arrayName]: rowsCopy,
    }));
    deleted.deactivate_until = undefined;
    updateSubscribers(subscriber_update_api_location, deleted, 'delete');
  };

  handleConditionTextChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist();
    const { name, value } = e.target;

    let conditionsCopy = JSON.parse(JSON.stringify(this.state.conditions));
    conditionsCopy[index][name] = value;
    this.setState((state) => ({
      ...state,
      conditions: conditionsCopy,
    }));
  };

  handleConditionTextBlur = (index: number, api_location: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const { alert_uid_hash, condition_api_location, conditions } = this.state;
    const { createNewCondition, updateCondition } = this.props;
    e.persist();
    const { name, value } = e.target;

    if (alert_uid_hash) {
      // In edit alert page
      if (api_location) {
        // Editing an existing condition
        updateCondition(api_location, name, value);
      } else {
        // Editing a newly created condition
        if (
          conditions[index]['name'] &&
          conditions[index]['data_point_tag'] &&
          conditions[index]['limit'] &&
          conditions[index]['comparison_operator'] &&
          conditions[index]['rolling_average_minutes']
        ) {
          createNewCondition(condition_api_location, conditions[index]);
        }
      }
    }

    let conditionsCopy = JSON.parse(JSON.stringify(this.state.conditions));
    conditionsCopy[index][name] = value;
    this.setState((state) => ({
      ...state,
      conditions: conditionsCopy,
    }));
  };

  onConditionSelectChange = (index: number, api_location: string, option: any, field: string) => {
    const { alert_uid_hash, condition_api_location, conditions } = this.state;
    const { dataTags, createNewCondition, updateCondition } = this.props;
    const tempDataTag = dataTags.find((tag) => tag.slug === option) || dataTags[0];

    if (alert_uid_hash) {
      // In edit alert page
      if (api_location) {
        // Editing an existing condition
        updateCondition(api_location, field, option);
      } else {
        // Editing a newly created condition
        if (
          conditions[index]['name'] &&
          conditions[index]['data_point_tag'] &&
          conditions[index]['limit'] &&
          conditions[index]['comparison_operator'] &&
          conditions[index]['rolling_average_minutes']
        ) {
          createNewCondition(condition_api_location, conditions[index]);
        }
      }
    }

    let conditionsCopy = JSON.parse(JSON.stringify(this.state.conditions));
    conditionsCopy[index][field] = option;
    conditionsCopy[index].measurement_unit = tempDataTag.measurement_unit;
    this.setState((state) => ({
      ...state,
      conditions: conditionsCopy,
    }));
  };

  handleSubscriberTextChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const { updateSubscribers } = this.props;
    const { subscriber_update_api_location } = this.state;
    e.persist();
    const { name, value } = e.target;

    let subscribersCopy = JSON.parse(JSON.stringify(this.state.subscribers));
    subscribersCopy[index][name] = value;
    this.setState((state) => ({
      ...state,
      subscribers: subscribersCopy,
    }));
    if (subscribersCopy[index].user != '' && subscribersCopy[index].method != '') {
      updateSubscribers(subscriber_update_api_location, subscribersCopy, 'merge');
    }
  };

  onSubscriberSelectChange = (index: number, option: any, field: string) => {
    const { subscriberUsers, updateSubscribers } = this.props;
    const { subscriber_update_api_location } = this.state;
    const tempSubscriberUser = subscriberUsers.find((user) => user.user === option) || subscriberUsers[0];

    let subscribersCopy = JSON.parse(JSON.stringify(this.state.subscribers));

    subscribersCopy[index][field] = option;

    if (field !== 'method' && field !== 'deactivate_until') {
      subscribersCopy[index].user = tempSubscriberUser.user;
      subscribersCopy[index].name = tempSubscriberUser.name;
      subscribersCopy[index].email = tempSubscriberUser.email;
      subscribersCopy[index].phone_number = tempSubscriberUser.phone_number;
    }

    this.setState((state) => ({
      ...state,
      subscribers: subscribersCopy,
    }));
    if (subscribersCopy[index].user != '' && subscribersCopy[index].method != '') {
      updateSubscribers(subscriber_update_api_location, subscribersCopy[index], 'merge');
    }
  };

  onContextSelectChange = (index: number, option: any, api_location: string) => {
    const { alert_uid_hash, context_api_location } = this.state;
    const { createNewContext, updateContext } = this.props;
    let contextCopy = JSON.parse(JSON.stringify(this.state.contexts));
    contextCopy[index]['data_point_tag'] = option;

    if (alert_uid_hash) {
      // In edit alert page
      if (api_location) {
        // Editing an existing context
        updateContext(api_location, option);
      } else {
        // Creating a new context
        createNewContext(context_api_location, option);
      }
    } else {
      this.setState((state) => ({
        ...state,
        contexts: contextCopy,
      }));
    }
  };

  validateFields() {
    const { name, message, threshold, autoResolveThreshold } = this.state;
    const isNameValid: boolean = name.length !== 0;
    const isMessageValid: boolean = message.length !== 0;
    const isDescriptionValid: boolean = true;
    const isThresholdValid: boolean = !isNaN(Number(threshold)) && threshold.length !== 0;
    const isAutoResolveThresholdValid: boolean =
      !isNaN(Number(autoResolveThreshold)) && autoResolveThreshold.length !== 0;

    this.setState((state) => ({
      ...state,
      errors: {
        name: !isNameValid,
        message: !isMessageValid,
        description: !isDescriptionValid,
        threshold: !isThresholdValid,
        autoResolveThreshold: !isAutoResolveThresholdValid,
      },
    }));
  }

  hasKey<O>(obj: O, key: keyof any): key is keyof O {
    return key in obj;
  }

  getInputClassName = (field: string) => {
    const { errors, touched } = this.state;

    if (this.hasKey(errors, field) && this.hasKey(touched, field)) {
      const hasError = errors[field];
      const shouldShow = touched[field];

      if (shouldShow) {
        if (hasError) {
          return 'alert-configuration__text-input alert-configuration__text-input-error';
        }

        return 'alert-configuration__text-input alert-configuration__text-input-success';
      }

      return 'alert-configuration__text-input';
    }
  };

  isAllValid() {
    const { errors, touched, alert_uid_hash } = this.state;
    return Object.keys(errors).map((i: string) => {
      if (this.hasKey(errors, i)) {
        return errors[i] === false && (touched[i] === true || alert_uid_hash);
      }
    });
  }

  updateAlert = () => {
    const {
      building: { identifier: buildingId },
      updateAlert,
    } = this.props;
    const {
      name,
      message,
      description,
      alertType,
      severityLevel,
      isActive,
      startDate,
      endDate,
      threshold,
      autoResolveThreshold,
      alert_uid_hash,
    } = this.state;

    const boolIsActive = isActive === 'yes';

    updateAlert(
      alert_uid_hash,
      buildingId,
      name,
      message,
      description,
      alertType,
      severityLevel,
      boolIsActive,
      startDate,
      endDate,
      threshold,
      autoResolveThreshold,
    );
  };

  createAlert = async () => {
    const {
      building: { identifier: buildingId },
      createAlert,
    } = this.props;
    const {
      name,
      message,
      description,
      alertType,
      severityLevel,
      isActive,
      startDate,
      endDate,
      threshold,
      autoResolveThreshold,
      conditions,
      subscribers,
      contexts,
    } = this.state;

    const nonEmptyConditions = conditions.filter((condition: any) => condition.data_point_tag !== '');
    const nonEmptySubscribers = subscribers.filter((subscriber: any) => subscriber.name !== '');
    const nonEmptyContexts = contexts.filter((context: any) => context.data_point_tag !== '');
    const boolIsActive = isActive === 'yes';

    const uid = await createAlert(
      buildingId,
      name,
      message,
      description,
      alertType,
      severityLevel,
      boolIsActive,
      startDate,
      endDate,
      threshold,
      autoResolveThreshold,
      nonEmptyConditions,
      nonEmptySubscribers,
      nonEmptyContexts,
    );

    this.props.history.push(`/${buildingId}/alerts/alert-setup/?id=${uid}`);
  };

  resetConditions = async () => {
    const { clearAlertsError } = this.props;

    await clearAlertsError();

    this.setState(initialState);
  };

  renderConditions = () => {
    const { conditions } = this.state;
    const { dataTags } = this.props;
    const validDataTags = this.validDataTags(dataTags);

    if (conditions.length === 0) {
      return (
        <div className="alert-configuration__legend-item">
          <Notification type="info" message="No conditions." />
        </div>
      );
    }

    return (
      <AlertConfigurationConditions
        conditions={conditions}
        validDataTags={validDataTags}
        handleConditionTextChange={this.handleConditionTextChange}
        handleConditionTextBlur={this.handleConditionTextBlur}
        onConditionSelectChange={this.onConditionSelectChange}
        removeRow={this.removeConditionRow}
        dataTags={dataTags}
      />
    );
  };

  renderSubscribers = () => {
    const { subscribers } = this.state;
    const { subscriberUsers } = this.props;
    const validSubscribers = this.validSubscribers(subscriberUsers);

    if (subscribers.length === 0) {
      return (
        <div className="alert-configuration__legend-item">
          <Notification type="info" message="No subscribers." />
        </div>
      );
    }

    return (
      <AlertConfigurationSubscribers
        subscribers={subscribers}
        validSubscribers={validSubscribers}
        handleSubscriberTextChange={this.handleSubscriberTextChange}
        onSubscriberSelectChange={this.onSubscriberSelectChange}
        removeRow={this.removeRow}
      />
    );
  };

  renderContext = () => {
    const { contexts } = this.state;
    const { dataTags } = this.props;
    const validDataTags = this.validDataTags(dataTags);

    if (contexts.length === 0) {
      return (
        <div className="alert-configuration__legend-item">
          <Notification type="info" message="No context." />
        </div>
      );
    }

    return (
      <div>
        {contexts.map((contextItem, i) => {
          const { data_point_tag, api_location } = contextItem;

          return (
            <div key={i} className="alert-configuration__legend-item">
              <div className="alert-configuration__alert-condition-input-wrapper">
                <Select
                  id={`tag${i}-select`}
                  options={validDataTags}
                  styles={customStyles}
                  className="alert-configuration__select-dropdown"
                  onChange={(selectedOption: any) => {
                    if (Array.isArray(selectedOption)) {
                      throw new Error('Unexpected type passed to ReactSelect onChange handler');
                    }

                    this.onContextSelectChange(i, selectedOption.value, api_location);
                  }}
                  defaultValue={validDataTags.find((dataTag: DataTagOptionType) => {
                    return dataTag.value === data_point_tag;
                  })}
                />
              </div>
              <div className="alert-configuration__alert-condition-input-wrapper">
                <div className="alert-configuration__alert-icon">
                  <span
                    className="alert-configuration__alert-condition-button"
                    onClick={() => this.removeContextRow(i, contexts)}
                  >
                    <FontAwesomeIcon icon={faTrash} size="lg" color="#0295a6" />
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  fieldToOption(value: any) {
    const valueString = value.toString();
    return {
      value: value,
      label: valueString.charAt(0).toUpperCase() + valueString.slice(1),
    };
  }

  redirectToList(buildingIdentifier: string) {
    this.props.history.push(`/${buildingIdentifier}/alerts/alert-list/`);
  }

  renderButtons(allFieldsValid: boolean) {
    const {
      alertCreated,
      building: { identifier },
    } = this.props;
    const { alert_uid_hash } = this.state;

    if (alert_uid_hash) {
      return (
        <React.Fragment>
          <div className="alert-configuration__button-cancel">
            <PrimaryButton label="Cancel" onClick={() => this.redirectToList(identifier)} fullWidth={true} />
          </div>
          <div className="alert-configuration__button">
            <PrimaryButton label="Update Alert" onClick={this.updateAlert} fullWidth={true} />
          </div>
        </React.Fragment>
      );
    } else {
      if (alertCreated) {
        return (
          <div className="alert-configuration__button">
            <PrimaryButton label="Create New" onClick={this.resetConditions} fullWidth={true} />
          </div>
        );
      } else {
        return (
          <div className="alert-configuration__button">
            <PrimaryButton
              label="Create Alert"
              isDisabled={!allFieldsValid}
              onClick={this.createAlert}
              fullWidth={true}
            />
          </div>
        );
      }
    }
  }

  render() {
    const {
      building: { name: buildingName },
      alertCreated,
      alertEdited,
      alertsError,
      isLoading,
    } = this.props;
    const {
      name,
      message,
      description,
      startDate,
      endDate,
      threshold,
      autoResolveThreshold,
      focusedInput,
      alertType,
      isActive,
      severityLevel,
    } = this.state;
    const allFieldsValid = this.isAllValid().every((i) => {
      return i === true;
    });

    const activeOption = isActive === undefined ? undefined : this.fieldToOption(isActive);
    const severityLevelOption = severityLevel === '' ? undefined : this.fieldToOption(severityLevel);
    const alertTypeOption = alertType === '' ? undefined : this.fieldToOption(alertType);

    if (isLoading) {
      return (
        <div className="alert-configuration">
          <div className="alert-configuration__header">
            <div className="alert-configuration__title">{buildingName} - Alert Configuration</div>
          </div>
          <div className="alert-configuration--loading">
            <Spinner />
          </div>
          <div className="alert-configuration__buttons">
            <div className="alert-configuration__button">
              <PrimaryButton
                icon="add-icon"
                label="Create Condition"
                onClick={() => this.createAlert()}
                fullWidth={true}
              />
            </div>
          </div>
        </div>
      );
    }

    if (alertsError) {
      toast.error(alertsError);
    }

    if (alertCreated) {
      toast.success('Alert successfully created!');
    } else if (alertEdited) {
      toast.success('Alert successfully updated!');
    }

    return (
      <div className="alert-configuration">
        <ToastContainer />
        <div className="alert-configuration__header">
          <div className="alert-configuration__title">{buildingName} - Alert Configuration</div>
          <div className="alert-configuration__title--mobile">Alert Configuration</div>
        </div>
        <div className="alert-configuration__main-content">
          <div className="alert-configuration__alert-info-section">
            <div className="alert-configuration__alert-info-section-row">
              <div className="alert-configuration__input-wrapper">
                <label className="alert-configuration__input-label">
                  Name:
                  <input
                    type="text"
                    id="name-input"
                    className={this.getInputClassName('name')}
                    name="name"
                    value={name}
                    onChange={(e) => this.handleTextChange(e)}
                    onBlur={() => this.handleBlur('name')}
                  />
                </label>
              </div>
              <div className="alert-configuration__input-wrapper">
                <label className="alert-configuration__input-label">
                  Message:
                  <input
                    type="text"
                    id="message-input"
                    className={this.getInputClassName('message')}
                    name="message"
                    value={message}
                    onChange={(e) => this.handleTextChange(e)}
                    onBlur={() => this.handleBlur('message')}
                  />
                </label>
              </div>
              <div className="alert-configuration__input-wrapper">
                <label className="alert-configuration__input-label">
                  Description:
                  <input
                    type="text"
                    id="description-input"
                    className="alert-configuration__text-input"
                    name="description"
                    value={description}
                    onChange={(e) => this.handleTextChange(e)}
                    onBlur={() => this.handleBlur('description')}
                  />
                </label>
              </div>
            </div>
            <div className="alert-configuration__alert-info-section-row">
              <div className="alert-configuration__input-wrapper">
                <label className="alert-configuration__input-label">
                  Alert Type:
                  <Select
                    id="alerttype-select"
                    options={alertTypeOptions}
                    styles={customStyles}
                    className="alert-configuration__select-dropdown"
                    value={alertTypeOption}
                    onChange={(selectedOption: any) => {
                      if (Array.isArray(selectedOption)) {
                        throw new Error('Unexpected type passed to ReactSelect onChange handler');
                      }

                      this.onSelectChange(selectedOption.value, 'alertType');
                    }}
                  />
                </label>
              </div>
              <div className="alert-configuration__input-wrapper">
                <label className="alert-configuration__input-label">
                  Severity:
                  <Select
                    id="severity-select"
                    options={severityLevelOptions}
                    styles={customStyles}
                    value={severityLevelOption}
                    className="alert-configuration__select-dropdown"
                    onChange={(selectedOption: any) => {
                      if (Array.isArray(selectedOption)) {
                        throw new Error('Unexpected type passed to ReactSelect onChange handler');
                      }

                      this.onSelectChange(selectedOption.value, 'severityLevel');
                    }}
                  />
                </label>
              </div>
              <div className="alert-configuration__input-wrapper">
                <label className="alert-configuration__input-label">
                  Is Alert Active?
                  <Select
                    id="active-select"
                    options={[
                      {
                        value: 'no',
                        label: 'No',
                      },
                      {
                        value: 'yes',
                        label: 'Yes',
                      },
                    ]}
                    styles={customStyles}
                    className="alert-configuration__select-dropdown"
                    value={activeOption}
                    onChange={(selectedOption: any) => {
                      if (Array.isArray(selectedOption)) {
                        throw new Error('Unexpected type passed to ReactSelect onChange handler');
                      }

                      this.onSelectChange(selectedOption.value, 'isActive');
                    }}
                  />
                </label>
              </div>
            </div>
            <div className="alert-configuration__alert-info-section-row">
              <div className="alert-configuration__input-wrapper">
                <label className="alert-configuration__input-label">
                  Threshold:
                  <input
                    type="text"
                    id="threshold-input"
                    className={this.getInputClassName('threshold')}
                    name="threshold"
                    value={threshold}
                    onChange={(e) => this.handleTextChange(e)}
                    onBlur={() => this.handleBlur('threshold')}
                  />
                </label>
              </div>
              <div className="alert-configuration__input-wrapper">
                <label className="alert-configuration__input-label">
                  Auto Resolve Threshold:
                  <input
                    type="text"
                    id="autoResolveThreshold-input"
                    className={this.getInputClassName('autoResolveThreshold')}
                    name="autoResolveThreshold"
                    value={autoResolveThreshold}
                    onChange={(e) => this.handleTextChange(e)}
                    onBlur={() => this.handleBlur('autoResolveThreshold')}
                  />
                </label>
              </div>
              <div className="alert-configuration__input-wrapper">
                <div className="alert-date-picker-wrapper">
                  <label className="alert-configuration__input-label">
                    Active from:
                    <DateRangePicker
                      startDate={startDate ? moment(startDate) : null} // momentPropTypes.momentObj or null,
                      startDateId={startDate || ''} // PropTypes.string.isRequired,
                      endDate={endDate ? moment(endDate) : null} // momentPropTypes.momentObj or null,
                      endDateId={endDate || ''} // PropTypes.string.isRequired,
                      onDatesChange={({ startDate, endDate }) => this.changeDates(startDate, endDate)} // PropTypes.func.isRequired,
                      focusedInput={focusedInput} // PropTypes.oneOf([START_DATE, END_DATE]) or null,
                      onFocusChange={(focusedInput) => this.setState((state) => ({ ...state, focusedInput }))} // PropTypes.func.isRequired,
                      isOutsideRange={falseFunc}
                      hideKeyboardShortcutsPanel={true}
                      numberOfMonths={1}
                      disableScroll={false}
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="alert-configuration__alert-condition-section">
            <div className="alert-configuration__legend">
              <div className="alert-configuration__legend-header">
                <div className="alert-configuration__legend-title">Conditions</div>
                <PrimaryButton label="Add row" onClick={this.addConditionRow} />
              </div>
              <div className="alert-configuration__column-names">
                <div className="equal-width">NAME</div>
                <div className="equal-width desktop-only">DATA TAG</div>
                <div className="equal-width mobile-only">TAG</div>
                <div className="equal-width">OPERATOR</div>
                <div className="equal-width">LIMIT</div>
                <div className="equal-width desktop-only">ROLLING AVG MINUTES</div>
                <div className="equal-width mobile-only">RAM</div>
                <div className="equal-width" />
              </div>
              {this.renderConditions()}
            </div>
          </div>
          <div className="alert-configuration__alert-subscribers-section">
            <div className="alert-configuration__legend">
              <div className="alert-configuration__legend-header">
                <div className="alert-configuration__legend-title">Subscribers</div>
                <PrimaryButton label="Add row" onClick={this.addSubscriberRow} />
              </div>
              <div className="alert-configuration__column-names">
                <div className="equal-width">NAME</div>
                <div className="equal-width desktop-only">PHONE NUMBER</div>
                <div className="equal-width desktop-only">EMAIL</div>
                <div className="equal-width desktop-only">FREQUENCY (min)</div>
                <div className="equal-width mobile-only">FREQ. (min)</div>
                <div className="equal-width desktop-only">NOTIFICATIONS</div>
                <div className="equal-width mobile-only">NOTIF.</div>
                <div className="equal-width">SNOOZE FOR</div>
                <div className="equal-width" />
              </div>
              {this.renderSubscribers()}
            </div>
          </div>
          <div className="alert-configuration__alert-context-section">
            <div className="alert-configuration__legend">
              <div className="alert-configuration__legend-header">
                <div className="alert-configuration__legend-title">Context</div>
                <PrimaryButton label="Add row" onClick={this.addContextRow} />
              </div>
              <div className="alert-configuration__column-names">
                <div className="equal-width">NAME</div>
                <div className="equal-width" />
              </div>
              {this.renderContext()}
            </div>
          </div>
        </div>
        <div className="alert-configuration__buttons">{this.renderButtons(allFieldsValid)}</div>
      </div>
    );
  }
}

export function mapStateToProps(state: any) {
  return {
    building: state.buildingReducer.selectedBuilding,
    dataTags: state.alertsReducer.registerSlugs,
    subscriberUsers: state.alertsReducer.subscribers,
    alertsError: state.alertsReducer.alertsReducer,
    alertCreated: state.alertsReducer.alertConditionCreated,
    alertEdited: state.alertsReducer.alertConditionEdited,
  };
}

const mapDispatchToProps = {
  getDataTags: AlertsActions.getRegisterSlugs,
  getSubscribers: AlertsActions.getSubscribers,
  updateSubscribers: AlertsActions.updateSubscribers,
  createNewContext: AlertsActions.createNewContext,
  updateContext: AlertsActions.updateContext,
  removeContext: AlertsActions.removeContext,
  createNewCondition: AlertsActions.createNewCondition,
  updateCondition: AlertsActions.updateCondition,
  removeCondition: AlertsActions.removeCondition,
  createAlert: AlertsActions.createAlert,
  updateAlert: AlertsActions.updateAlert,
  fetchAlert: AlertsActions.fetchAlert,
  clearAlertsError: AlertsActions.clearAlertsError,
  clearCreateAlertConditionSuccess: AlertsActions.clearCreateAlertConditionSuccess,
  clearEditAlertConditionSuccess: AlertsActions.clearEditAlertConditionSuccess,
};

export const ConnectedAlertConfigurationWithRouter = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
)(AlertConfiguration);
